import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import {
  AppShell,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Container,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Header } from "../components/layout/Header";
import { useCurrentUser } from "../hooks/queries/useCurrentUser";
import { UserRole, getRoleName } from "../models/user.model";
import { GraphService, UserProfileData } from "../services/graphService";
import { UserService } from "../services/userService";

export function ProfilePage() {
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { isAuthenticated, currentUser, userRole, updateUserRole } = useAuth();
  const navigate = useNavigate();
  const graphService = new GraphService();
  const { data: reactQueryUser, isLoading: userLoading } = useCurrentUser();

  async function loadUserProfile() {
    try {
      setLoading(true);
      setError(null);

      // Load user profile from MS Graph using the optimized method
      const userProfileData = await graphService.getSelectedUserInfo();
      setProfile(userProfileData);
      console.log("User profile data from Graph API:", userProfileData);

      // Load user photo
      const photo = await graphService.getCurrentUserPhoto();
      setPhotoUrl(photo);
    } catch (err) {
      console.error("Error loading profile data:", err);
      setError("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  }

  // Load user role data when profile page loads
  useEffect(() => {
    if (isAuthenticated && !userLoading && !userRole && reactQueryUser) {
      console.log("User role data loaded for profile page:", reactQueryUser);
      updateUserRole(reactQueryUser);
    }
  }, [isAuthenticated, userRole, reactQueryUser, userLoading]);

  // Auto-fill name from domain if missing — runs silently when user data is loaded
  useEffect(() => {
    if (!isAuthenticated || userLoading || !reactQueryUser) {
      return;
    }

    if (!reactQueryUser.firstName && !reactQueryUser.lastName) {
      const userService = new UserService();
      userService.syncDisplayName().catch((err) => {
        console.warn("Auto name sync from domain failed:", err);
      });
    }
  }, [isAuthenticated, userLoading, reactQueryUser]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    loadUserProfile();
  }, [isAuthenticated]);

  const handleBack = () => {
    navigate(-1);
  };



  // Function to render employee details in a consistent format
  const renderEmployeeDetail = (
    label: string,
    value: string | null | undefined
  ) => (
    <Group>
      <Box w={200}>
        <Text fw={500}>{label}</Text>
      </Box>
      <Text>{value || "Not specified"}</Text>
    </Group>
  );

  // Get color for role badge
  const getRoleBadgeColor = (): string => {
    if (!userRole) return "gray";

    switch (userRole) {
      case UserRole.Admin:
        return "red";
      case UserRole.Manager:
        return "blue";
      case UserRole.User:
        return "gray";
      default:
        return "gray";
    }
  };

  return (
    <AppShell header={{ height: 60 }} padding="md">
      <AppShell.Header>
        <Header />
      </AppShell.Header>
      <AppShell.Main>
        <Container size="md" py="xl">
          <Group justify="space-between" mb="md">
            <Button
              variant="subtle"
              leftSection={<ArrowLeftIcon width={16} />}
              onClick={handleBack}
            >
              Back
            </Button>
          </Group>

          <Title order={2} mb="lg">
            My Profile
          </Title>

          {/* User details panel */}
          <Paper p="md" withBorder mb="xl">
            <Title order={3} mb="md">
              User Information
            </Title>

            {loading ? (
              <Center py="xl">
                <Loader />
              </Center>
            ) : error ? (
              <Text c="red">{error}</Text>
            ) : profile ? (
              <Stack>
                <Group align="flex-start">
                  <Avatar src={photoUrl} size={100} radius="md" />
                  <Box>
                    <Title order={4}>{profile.displayName}</Title>
                    <Text>{profile.jobTitle || "No job title specified"}</Text>
                    <Text c="dimmed">{profile.mail}</Text>

                    {currentUser && (
                      <Group mt="xs">
                        <Text fw={500}>Person ID:</Text>
                        <Text>{currentUser.personId}</Text>
                      </Group>
                    )}

                    <Group mt="xs">
                      <Text fw={500}>Role:</Text>
                      {userLoading ? (
                        <Loader size="xs" />
                      ) : userRole ? (
                        <Badge color={getRoleBadgeColor()}>
                          {getRoleName(userRole)}
                        </Badge>
                      ) : (
                        <Text c="dimmed">Not available</Text>
                      )}
                    </Group>

                  </Box>
                </Group>

                <Divider my="md" />

                <Stack gap="md">
                  {renderEmployeeDetail("Company", profile.companyName)}
                  {renderEmployeeDetail("Department", profile.department)}
                  {renderEmployeeDetail(
                    "Office Location",
                    profile.officeLocation
                  )}

                  {profile.businessPhones &&
                    profile.businessPhones.length > 0 &&
                    renderEmployeeDetail(
                      "Business Phone",
                      profile.businessPhones[0]
                    )}

                  {profile.mobilePhone &&
                    renderEmployeeDetail("Mobile Phone", profile.mobilePhone)}
                </Stack>
              </Stack>
            ) : (
              <Text>No profile data available</Text>
            )}
          </Paper>
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}
